import React, { useState, useEffect } from "react";
import { Card, Row, Col, Form, Button, Table, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Swal from 'sweetalert2';
import { COLORS, API_URL } from "../../types/profession.types";
import AddClientModal from "../../../../components/SendToClient/AddClientModal";

export const ImportClientsTab: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importedData, setImportedData] = useState<any[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingClient, setEditingClient] = useState<any>(null);
  const [directoryClients, setDirectoryClients] = useState<any[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const LIMIT = 20;

  const fetchDirectoryClients = async (page = 1) => {
    try {
      setLoadingClients(true);
      const token = localStorage.getItem("token");

      const response = await axios.get(`${API_URL}/clients/all`, {
        headers: { Authorization: token },
        params: { page, limit: LIMIT }
      });

      if (response.data.success) {
        setDirectoryClients(response.data.data);
        setCurrentPage(response.data.pagination.page);
        setTotalPages(response.data.pagination.totalPages);
        setTotalClients(response.data.pagination.total);
      }
    } catch (error: any) {
      toast.error("Erreur lors du chargement des clients");
    } finally {
      setLoadingClients(false);
    }
  };

  useEffect(() => {
    fetchDirectoryClients(1);
  }, []);

  const handleClientAdded = (client: any) => {
    toast.success("Client ajouté avec succès");
    fetchDirectoryClients();
  };

  const handleOpenAddModal = () => {
    setEditingClient(null);
    setShowAddModal(true);
  };

  const downloadTemplate = () => {
    Swal.fire({
      title: '📋 Modèle d\'import',
      html: `
        <div style="text-align: left;">
          <p style="margin-bottom: 15px; font-size: 1.1rem;">
            ⚠️ <strong>Important :</strong> Pour un import réussi, respectez scrupuleusement la structure du modèle.
          </p>
          <div style="background-color: #f8f9fa; padding: 15px; border-radius: 8px; margin-bottom: 15px;">
            <p style="font-weight: 600; margin-bottom: 10px; color: #4bb0c0;">📌 Règles à suivre :</p>
            <ul style="list-style: none; padding-left: 0; margin-bottom: 0;">
              <li style="margin-bottom: 8px;">✓ <strong>Ne modifiez pas les en-têtes</strong> (ils doivent rester identiques)</li>
              <li style="margin-bottom: 8px;">✓ Remplissez les colonnes avec vos données</li>
              <li style="margin-bottom: 8px;">✓ Les champs obligatoires : <span style="color: #dc3545;">Email, NOM, PRÉNOM</span></li>
              <li style="margin-bottom: 8px;">✓ Format de fichier accepté : <strong>.xlsx, .xls, .csv</strong></li>
              <li style="margin-bottom: 8px;">✓ Vous pouvez supprimer les lignes d'exemple</li>
            </ul>
          </div>
          <p style="color: #6c757d; font-size: 0.9rem; margin-bottom: 0;">
            Une fois votre fichier prêt, vous pourrez l'importer sur cette même page.
          </p>
        </div>
      `,
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: '📥 Télécharger le modèle',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#4bb0c0',
      cancelButtonColor: '#6c757d',
      width: 600,
      padding: '20px',
    }).then((result) => {
      if (result.isConfirmed) {
        const template = [
          { "Email": "jean.dupont@email.com", "PROFESSION": "Comptable", "NOM": "Dupont", "PRÉNOM": "Jean", "ADRESSE": "15 rue de la Paix", "CODE POSTAL": "75001", "VILLE": "Paris", "TÉLÉPHONE": "0612345678", "FORME JURIDIQUE": "SARL", "DIRIGEANT": "Jean Dupont", "IMMATRICULATION": "123456789", "ANNEE_CA": "2023", "CA": "150000", "RÉSULTAT": "25000", "EFFECTIF": "10-19 salariés", "NAF APE": "6201Z", "SIRET": "12345678901234", "EFFECTIF MIN": "10", "EFFECTIF MAX": "19" }
        ];

        const worksheet = XLSX.utils.json_to_sheet(template);
        const headers = Object.keys(template[0]);
        
        headers.forEach((header, index) => {
          const cellAddress = XLSX.utils.encode_cell({ r: 0, c: index });
          if (!worksheet[cellAddress]) return;
          worksheet[cellAddress].v = header.toUpperCase();
          worksheet[cellAddress].s = {
            font: { bold: true, color: { rgb: "FFFFFF" } },
            fill: { fgColor: { rgb: "4BB0C0" } },
            alignment: { horizontal: "center" }
          };
        });

        worksheet["!cols"] = headers.map(() => ({ wch: 30 }));

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Modèle import");
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array", cellStyles: true });
        const file = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

        saveAs(file, `modele_import_clients_${new Date().toISOString().slice(0, 10)}.xlsx`);
        
        Swal.fire({
          title: '✅ Modèle téléchargé',
          text: 'Remplissez-le avec vos données puis revenez l\'importer',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      }
    });
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFile(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet);
          setImportedData(jsonData);
        } catch (error) {
          toast.error("Erreur lors de la lecture du fichier");
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const handleImport = async () => {
    if (importedData.length === 0) {
      return toast.error("Aucun client à importer");
    }

    try {
      setImporting(true);
      const token = localStorage.getItem("token");

      const response = await axios.post(
        `${API_URL}/clients/import`,
        { clients: importedData },
        {
          headers: { 'Content-Type': 'application/json', Authorization: token },
        }
      );

      if (response.data.success) {
        toast.success(`${response.data.count} clients importés avec succès`);
        setFile(null);
        setImportedData([]);
        const fileInput = document.getElementById('file-upload') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
        fetchDirectoryClients();
      } else {
        toast.error(response.data.message || "Erreur lors de l'import");
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Erreur lors de l'import");
    } finally {
      setImporting(false);
    }
  };

  const handleFileSelectClick = () => {
    document.getElementById('file-upload')?.click();
  };

  const handleDeleteClient = async (id: number) => {
    if (!window.confirm("Voulez-vous vraiment supprimer ce client ?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/clients/${id}`, {
        headers: { Authorization: token },
      });
      toast.success("Client supprimé avec succès");
      fetchDirectoryClients();
    } catch (error: any) {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <>
      {/* Header avec titre et bouton d'action */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
        <div>
          <h2 className="h4 fw-semibold mb-1" style={{ color: COLORS.text }}>
            Import de clients
          </h2>
          <p className="text-muted" style={{ fontSize: "0.9rem" }}>
            Importez vos clients en masse à partir d'un fichier Excel
          </p>
        </div>
        
        <Button
          onClick={handleOpenAddModal}
          variant="primary"
          className="mt-3 mt-md-0"
          style={{
            backgroundColor: COLORS.primary,
            borderColor: COLORS.primary,
            padding: "0.6rem 1.5rem",
            fontWeight: 500,
            borderRadius: "8px"
          }}
        >
          <i className="bi bi-plus-circle me-2"></i>
          Nouveau client
        </Button>
      </div>

      {/* Cartes d'étapes */}
      <Row className="g-4 mb-4">
        <Col md={6}>
          <Card className="h-100 border-0 shadow-sm" style={{ borderRadius: "12px" }}>
            <Card.Body className="p-4">
              <div className="d-flex align-items-center mb-3">
                <div 
                  className="rounded-circle d-flex align-items-center justify-content-center me-3"
                  style={{ width: "40px", height: "40px", backgroundColor: "#f0f9ff", color: COLORS.primary, fontSize: "1.2rem", fontWeight: "600" }}
                >
                  1
                </div>
                <h5 className="fw-semibold mb-0" style={{ color: COLORS.text }}>
                  Télécharger le modèle
                </h5>
              </div>
              <p className="text-muted small mb-3">
                Utilisez notre modèle Excel pour formater correctement vos données
              </p>
              <Button
                onClick={downloadTemplate}
                variant="outline-primary"
                className="w-100"
                style={{ borderColor: COLORS.primary, color: COLORS.primary, padding: "0.6rem", borderRadius: "8px", fontWeight: 500 }}
              >
                <i className="bi bi-file-earmark-spreadsheet me-2"></i>
                Télécharger le modèle
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="h-100 border-0 shadow-sm" style={{ borderRadius: "12px" }}>
            <Card.Body className="p-4">
              <div className="d-flex align-items-center mb-3">
                <div 
                  className="rounded-circle d-flex align-items-center justify-content-center me-3"
                  style={{ width: "40px", height: "40px", backgroundColor: "#f0f9ff", color: COLORS.primary, fontSize: "1.2rem", fontWeight: "600" }}
                >
                  2
                </div>
                <h5 className="fw-semibold mb-0" style={{ color: COLORS.text }}>
                  Importer vos clients
                </h5>
              </div>
              <p className="text-muted small mb-3">
                Sélectionnez votre fichier Excel rempli avec vos données
              </p>
              
              <Form.Group>
                <Form.Control
                  id="file-upload"
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileUpload}
                  className="py-2"
                  style={{ borderRadius: "8px", borderColor: COLORS.border, display: 'none' }}
                />
                
                <Button
                  onClick={handleFileSelectClick}
                  variant="outline-primary"
                  className="w-100"
                  style={{ borderColor: COLORS.primary, color: COLORS.primary, padding: "0.6rem", borderRadius: "8px", fontWeight: 500 }}
                >
                  <i className="bi bi-upload me-2"></i>
                  Sélectionner un fichier
                </Button>
                
                {file && (
                  <div className="mt-2 small text-muted d-flex align-items-center">
                    <i className="bi bi-check-circle-fill me-2" style={{ color: COLORS.success }}></i>
                    {file.name}
                  </div>
                )}
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Aperçu des données importées */}
      {importedData.length > 0 && (
        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "12px" }}>
          <Card.Header className="bg-white border-0 pt-4 px-4">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <h5 className="fw-semibold mb-1" style={{ color: COLORS.text }}>
                  Aperçu des données
                </h5>
                <p className="text-muted small mb-0">
                  {importedData.length} client(s) prêts à être importés
                </p>
              </div>
              <span className="badge bg-light text-dark px-3 py-2">
                {importedData.length} lignes
              </span>
            </div>
          </Card.Header>
          <Card.Body className="p-4 pt-2">
            <div className="table-responsive" style={{ maxHeight: "300px" }}>
              <Table hover size="sm" className="mb-0">
                <thead style={{ position: "sticky", top: 0, backgroundColor: "#f8f9fa", zIndex: 1 }}>
                  <tr>
                    {Object.keys(importedData[0]).slice(0, 6).map((key) => (
                      <th key={key} style={{ fontSize: "0.8rem", fontWeight: 600 }}>{key}</th>
                    ))}
                    {Object.keys(importedData[0]).length > 6 && (
                      <th style={{ fontSize: "0.8rem", fontWeight: 600, color: COLORS.textLight }}>
                        +{Object.keys(importedData[0]).length - 6} colonnes
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {importedData.slice(0, 5).map((row, idx) => (
                    <tr key={idx}>
                      {Object.values(row).slice(0, 6).map((val: any, i) => (
                        <td key={i} style={{ fontSize: "0.85rem", maxWidth: "150px" }} className="text-truncate">
                          {String(val)}
                        </td>
                      ))}
                      {Object.keys(row).length > 6 && (
                        <td style={{ fontSize: "0.85rem", color: COLORS.textLight }}>...</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            {importedData.length > 5 && (
              <div className="text-center mt-3">
                <small className="text-muted">
                  Affichage des 5 premières lignes sur {importedData.length}
                </small>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Actions d'import */}
      {importedData.length > 0 && (
        <div className="d-flex flex-column flex-sm-row justify-content-end gap-3">
          <Button
            onClick={() => {
              setFile(null);
              setImportedData([]);
              const fileInput = document.getElementById('file-upload') as HTMLInputElement;
              if (fileInput) fileInput.value = '';
            }}
            variant="outline-secondary"
            disabled={importing}
            style={{ padding: "0.6rem 1.8rem", borderRadius: "8px", fontWeight: 500, borderColor: COLORS.border, color: COLORS.textLight }}
          >
            Annuler
          </Button>

          <Button
            onClick={handleImport}
            disabled={importing}
            style={{ padding: "0.6rem 2rem", borderRadius: "8px", fontWeight: 500, backgroundColor: COLORS.primary, borderColor: COLORS.primary, opacity: importing ? 0.7 : 1, minWidth: "180px" }}
          >
            {importing ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                Import en cours...
              </>
            ) : (
              <>
                <i className="bi bi-cloud-upload me-2"></i>
                Importer les clients
              </>
            )}
          </Button>
        </div>
      )}

      {/* Modal d'ajout manuel */}
      <AddClientModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onClientAdded={handleClientAdded}
        editingClient={editingClient}
      />

      {/* Liste des clients */}
      <Card className="border-0 shadow-sm mt-5" style={{ borderRadius: "12px" }}>
        <Card.Header className="bg-white border-0 pt-4 px-4">
          <h5 className="fw-semibold mb-0">Clients visibles dans l'annuaire</h5>
        </Card.Header>

        <Card.Body>
          {loadingClients ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : directoryClients.length === 0 ? (
            <p className="text-muted">Aucun client visible</p>
          ) : (
            <>
              <div className="table-responsive" style={{ maxHeight: "500px", overflowY: "auto", borderRadius: "12px" }}>
                <Table hover className="modern-table" style={{ minWidth: "1000px" }}>
                  <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                    <tr>
                      <th style={{ background: COLORS.primary, color: "white", padding: "16px 12px", fontWeight: 600 }}>Nom & Prénom(s)</th>
                      <th style={{ background: COLORS.primary, color: "white", padding: "16px 12px", fontWeight: 600 }}>Entreprise</th>
                      <th style={{ background: COLORS.primary, color: "white", padding: "16px 12px", fontWeight: 600 }}>Profession</th>
                      <th style={{ background: COLORS.primary, color: "white", padding: "16px 12px", fontWeight: 600 }}>Email</th>
                      <th style={{ background: COLORS.primary, color: "white", padding: "16px 12px", fontWeight: 600 }}>Téléphone</th>
                      <th style={{ background: COLORS.primary, color: "white", padding: "16px 12px", fontWeight: 600, textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {directoryClients.map((client, index) => (
                      <tr key={client.id} style={{ backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8f9fa" }}>
                        <td style={{ padding: "14px 12px" }}>
                          {client.nom} {client.prenom}
                        </td>
                        <td style={{ padding: "14px 12px" }}>
                          {client.siren && (
                            <span style={{ fontSize: "0.75rem", backgroundColor: "#f1f3f5", padding: "2px 6px", borderRadius: "4px" }}>
                              SIREN: {client.siren}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "14px 12px" }}>
                          {client.profession ? (
                            <span style={{ backgroundColor: "#e7f5ff", color: "#1971c2", padding: "4px 10px", borderRadius: "20px", fontSize: "0.8rem" }}>
                              {client.profession}
                            </span>
                          ) : "—"}
                        </td>
                        <td style={{ padding: "14px 12px" }}>
                          <a href={`mailto:${client.email}`} style={{ color: COLORS.primary, textDecoration: "none" }}>
                            {client.email}
                          </a>
                        </td>
                        <td style={{ padding: "14px 12px" }}>
                          {client.telephone || "—"}
                        </td>
                        <td style={{ padding: "14px 12px", textAlign: "right" }}>
                          <div className="d-flex gap-2 align-items-center justify-content-end">
                            <button
                              className="btn btn-sm"
                              onClick={() => {
                                setEditingClient(client);
                                setShowAddModal(true);
                              }}
                              style={{ backgroundColor: "transparent", border: "none", color: COLORS.primary }}
                            >
                              <i className="bi bi-pencil-square" style={{ fontSize: "1.1rem" }}></i>
                            </button>
                            <button
                              className="btn btn-sm"
                              onClick={() => handleDeleteClient(client.id)}
                              style={{ backgroundColor: "transparent", border: "none", color: "#dc3545" }}
                            >
                              <i className="bi bi-trash3" style={{ fontSize: "1.1rem" }}></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="d-flex align-items-center justify-content-between mt-3 px-1">
                <span className="text-muted" style={{ fontSize: "0.875rem" }}>
                  {totalClients} client(s) — Page {currentPage} sur {totalPages}
                </span>
                <div className="d-flex gap-2">
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => fetchDirectoryClients(1)} disabled={currentPage === 1}>«</button>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => fetchDirectoryClients(currentPage - 1)} disabled={currentPage === 1}>‹ Précédent</button>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => fetchDirectoryClients(currentPage + 1)} disabled={currentPage === totalPages}>Suivant ›</button>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => fetchDirectoryClients(totalPages)} disabled={currentPage === totalPages}>»</button>
                </div>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </>
  );
};